/**
 * AddAdvancePaymentDialog — modal for adding an advance payment to a
 * patient's balance (Tier-3 patient_billing, PROD-XXX).
 *
 * Flutter ref: `patient_billing/.../widgets/add_balance_popup.dart`
 * (L19-299). Title "Add Payment", 3-method picker (Cash/Card/Bank — NO
 * Balance, since you can't pay into balance using balance), amount,
 * optional note, Approve/Cancel buttons.
 *
 * **Wire**: POST `/api/Billing/Payment/Create/Patient/{patientId}` body
 * `AdvancePaymentRequest` → `AdvancePaymentResponse`. See
 * `useCreatePatientAdvancePayment` for full wire shape.
 *
 * **Field rules** (verbatim from Flutter):
 *  - paymentMethod: required, one of "Cash" / "Card" / "Bank"
 *    (`scrips_core/lib/constants/app_constants.dart:744-746`)
 *  - paidAmount: > 0, decimal max 2 places. Validation message:
 *    "Amount must be greater than 0 with valid value"
 *  - note: optional, max 500 chars
 *  - receivedBy: current user's id (auth context)
 *  - receiptDate: client-now at submit time, no-Z ISO format to mirror
 *    the sibling AddPaymentRequest convention (see
 *    `shared/types/billing.ts:889`)
 *
 * **Button order**: Approve LEFT, Cancel RIGHT — matches Flutter
 * `add_balance_popup.dart:184-217` (and the sibling `add_payment_popup`
 * which is the established convention across all Flutter billing
 * popups). This is opposite the more common "Cancel-then-Confirm"
 * convention; we match Flutter because it's the established pattern in
 * billing-module dialogs.
 */

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BrandSpinner } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';

import { Button, Modal } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { useCreatePatientAdvancePayment } from '@/patients/services/use-create-patient-advance-payment';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type { AdvancePaymentRequest } from '@/shared/types/billing';

// ──────────────────────────────────────────────────────────────────────────
// Payment method magic strings — verbatim from Flutter
// scrips_core/lib/constants/app_constants.dart:744-746
// ──────────────────────────────────────────────────────────────────────────
const PAYMENT_METHOD_CASH = 'Cash';
const PAYMENT_METHOD_CARD = 'Card';
const PAYMENT_METHOD_BANK = 'Bank';

const METHODS = [
  { value: PAYMENT_METHOD_CASH, label: 'Cash' },
  { value: PAYMENT_METHOD_CARD, label: 'Card' },
  { value: PAYMENT_METHOD_BANK, label: 'Bank' },
] as const;

// ──────────────────────────────────────────────────────────────────────────
// Form schema
// ──────────────────────────────────────────────────────────────────────────

const schema = z.object({
  paymentMethod: z.enum([
    PAYMENT_METHOD_CASH,
    PAYMENT_METHOD_CARD,
    PAYMENT_METHOD_BANK,
  ]),
  /** Free-text amount string — validated to a positive number with up to
   *  2 decimal places. Empty / non-numeric is invalid (matches Flutter
   *  `add_balance_popup.dart:277-289`). */
  paidAmountText: z
    .string()
    .min(1, 'Amount must be greater than 0 with valid value')
    .refine(
      (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0;
      },
      { message: 'Amount must be greater than 0 with valid value' },
    )
    .refine(
      (v) => /^\d+(\.\d{1,2})?$/.test(v),
      { message: 'Amount must be greater than 0 with valid value' },
    ),
  note: z.string().max(500).optional(),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  paymentMethod: PAYMENT_METHOD_CASH,
  paidAmountText: '',
  note: '',
};

// ──────────────────────────────────────────────────────────────────────────
// Dialog
// ──────────────────────────────────────────────────────────────────────────

export interface AddAdvancePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
}

export function AddAdvancePaymentDialog({
  open,
  onOpenChange,
  patientId,
}: AddAdvancePaymentDialogProps) {
  // PROD-2743 · the DS BrandSpinner is the one inline loader.
  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  // Reset form whenever the dialog reopens. Mirrors Flutter's per-popup
  // `initState` defaults.
  useEffect(() => {
    if (open) form.reset(defaultValues);
  }, [open, form]);

  const createMut = useCreatePatientAdvancePayment();
  const isPending = createMut.isPending;

  const submit = form.handleSubmit((values) => {
    if (!user) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }
    if (!userId) {
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }

    // No-Z ISO at submit time, read off the LOCAL clock — Flutter's
    // `DateTime.now().toIso8601String()` emits local wall-clock with no `Z`,
    // and the .NET model binder for `receiptDate` binds the literal.
    //
    // PROD-1778: was `new Date().toISOString().replace(/Z$/, '')`. Stripping
    // the `Z` off a value `toISOString()` already converted to UTC does not
    // restore local time — it bakes the offset in, so 13:00 Asia/Dubai was
    // stored as 09:00. `formatLocalDatetimeForWire` reads local components.
    const receiptDate = formatLocalDatetimeForWire(new Date());

    const request: AdvancePaymentRequest = {
      paymentMethod: values.paymentMethod,
      paidAmount: Number(values.paidAmountText),
      note: values.note?.trim() || undefined,
      receivedBy: userId,
      receiptDate,
    };

    createMut.mutate(
      { patientId, request },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  });

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Add Payment"
      size="md"
      footer={<><Button
              type="submit"
              disabled={!form.formState.isValid || isPending}
            >
              {isPending ? (
                <>
                  <BrandSpinner size={16} label="Approving" className="mr-2" />
                  Approving…
                </>
              ) : (
                'Approve'
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button></>}
      footerAlign="start"
    >
        

        <form onSubmit={submit} data-slot="payment-form">
          {/* Method picker — 3-button row matching Flutter's
              SingleTagIconPicker visual. */}
          <Controller
            name="paymentMethod"
            control={form.control}
            render={({ field }) => (
              <div data-slot="payment-field">
                <span data-slot="payment-label">Method</span>
                <div data-slot="field-pills">
                  {METHODS.map((m) => {
                    const active = field.value === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => field.onChange(m.value)}
                        data-slot="field-pill"
                        data-active={active ? 'true' : 'false'}
                        aria-pressed={active}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          />

          <div data-slot="payment-row">
            {/* Amount */}
            <Controller
              name="paidAmountText"
              control={form.control}
              render={({ field, fieldState }) => (
                <div data-slot="payment-field">
                  <label data-slot="payment-label" htmlFor="advance-payment-amount">
                    Payment
                  </label>
                  <TextField
                    id="advance-payment-amount"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="Enter amount"
                    hideSpinButtons
                    {...field}
                  />
                  {fieldState.error?.message ? (
                    <p data-slot="payment-error">
                      {fieldState.error.message}
                    </p>
                  ) : null}
                </div>
              )}
            />

            {/* Note */}
            <Controller
              name="note"
              control={form.control}
              render={({ field, fieldState }) => (
                <div data-slot="payment-field">
                  <label data-slot="payment-label" htmlFor="advance-payment-note">
                    Note
                  </label>
                  <Textarea
                    id="advance-payment-note"
                    placeholder="Enter note"
                    maxLength={500}
                    rows={2}
                    className="resize-none"
                    {...field}
                  />
                  {fieldState.error?.message ? (
                    <p data-slot="payment-error">
                      {fieldState.error.message}
                    </p>
                  ) : null}
                </div>
              )}
            />
          </div>

          {/* Approve LEFT, Cancel RIGHT — matches Flutter billing-popup
              convention (add_balance_popup.dart:184-217). */}
          
        </form>
      </Modal>
  );
}
